import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { useTalmidim } from "../context/TalmidimContext";
import { usePasses } from "../context/PassesContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Search, FilePlus2, Upload, FileSpreadsheet, Download, Loader2, AlertCircle, Users, Trash2, CheckCircle2, UserPlus } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const EXPECTED_HEADERS = {
  name: ["name", "student", "student name", "kid", "kid name", "תלמיד", "נאמען", "שם"],
  klas: ["klas", "class", "grade", "כיתה", "כתה", "קלאס"],
  father: ["father", "פאטער", "אבא", "father name"],
  phone: ["phone", "טעלעפאן", "telephone", "cell"],
};

function normHeader(h) {
  return String(h || "").toLowerCase().replace(/[^a-zא-ת\s]/gi, "").trim();
}

function detectColumn(headers, keys) {
  for (let i = 0; i < headers.length; i++) {
    const h = normHeader(headers[i]);
    if (keys.some((k) => h === k.toLowerCase() || h.includes(k.toLowerCase()))) return i;
  }
  return -1;
}

export default function Talmidim() {
  const { talmidim, klasn, loading, refresh, bulkImport, addTalmid, deleteTalmid } = useTalmidim();
  const { passes } = usePasses();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [search, setSearch] = useState("");
  const [klasFilter, setKlasFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importReplace, setImportReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState("");

  // Add-Student dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addKlas, setAddKlas] = useState("");
  const [addNewKlas, setAddNewKlas] = useState("");
  const [addFather, setAddFather] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [adding, setAdding] = useState(false);

  const countsByTalmid = useMemo(() => {
    const map = {};
    passes.forEach((p) => { map[p.talmidId] = (map[p.talmidId] || 0) + 1; });
    return map;
  }, [passes]);

  const filtered = useMemo(() => {
    return talmidim.filter((t) => {
      if (search && !(t.fullName || "").includes(search)) return false;
      if (klasFilter !== "all" && String(t.klasId) !== klasFilter) return false;
      return true;
    });
  }, [talmidim, search, klasFilter]);

  // Group filtered talmidim by klasName for accordion view
  const grouped = useMemo(() => {
    const map = new Map();
    // preserve klas order from klasn state
    klasn.forEach((k) => map.set(k.id, { klas: k, list: [] }));
    filtered.forEach((t) => {
      if (!map.has(t.klasId)) map.set(t.klasId, { klas: { id: t.klasId, name: t.klasName || "—" }, list: [] });
      map.get(t.klasId).list.push(t);
    });
    return Array.from(map.values()).filter((g) => g.list.length > 0);
  }, [filtered, klasn]);

  const handleFile = async (file) => {
    setParseError("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });

      if (!rows.length) {
        setParseError("די פייל איז ליידיג");
        return;
      }

      // Detect if first row is header
      const first = rows[0].map((c) => String(c || "").trim());
      const hasHeader = first.some((c) => /[a-zא-ת]/i.test(c) && c.length < 30);
      let nameIdx = 0;
      let klasIdx = 1;
      let fatherIdx = -1;
      let phoneIdx = -1;
      let dataStart = 0;

      if (hasHeader) {
        nameIdx = detectColumn(first, EXPECTED_HEADERS.name);
        klasIdx = detectColumn(first, EXPECTED_HEADERS.klas);
        fatherIdx = detectColumn(first, EXPECTED_HEADERS.father);
        phoneIdx = detectColumn(first, EXPECTED_HEADERS.phone);
        if (nameIdx === -1) nameIdx = 0;
        if (klasIdx === -1) klasIdx = 1;
        dataStart = 1;
      }

      const items = [];
      for (let i = dataStart; i < rows.length; i++) {
        const r = rows[i];
        if (!r || !r.length) continue;
        const name = String(r[nameIdx] ?? "").trim();
        const klas = String(r[klasIdx] ?? "").trim();
        if (!name) continue;
        items.push({
          name,
          klas: klas || "אנדערע",
          father: fatherIdx >= 0 ? String(r[fatherIdx] ?? "").trim() : "",
          phone: phoneIdx >= 0 ? String(r[phoneIdx] ?? "").trim() : "",
        });
      }

      if (!items.length) {
        setParseError("קיין תלמידים אין דעם פייל");
        return;
      }

      setImportPreview(items);
    } catch (e) {
      console.error(e);
      setParseError("קען נישט לייענען דעם פייל. פראבירט א .xlsx אדער .csv");
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const doImport = async () => {
    if (!importPreview.length) return;
    setImporting(true);
    try {
      const res = await bulkImport(importPreview, importReplace);
      toast({
        title: "אינפארטירט!",
        description: `${res.added} תלמידים ${importReplace ? "פארביטן" : "צוגעלייגט"} · ${res.klasnCreated} כיתות`,
      });
      setImportOpen(false);
      setImportPreview([]);
      setImportReplace(false);
    } catch (e) {
      toast({ title: "פעלער", description: e?.response?.data?.detail || "אימפארט האט נישט געארבעט", variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Student Name", "Class"],
      ["מרדכי גאלדבערג", "כתה א'"],
      ["שלמה פרידמאן", "כתה א'"],
      ["אברהם וויס", "כתה ב'"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Talmidim");
    XLSX.writeFile(wb, "talmidim-template.xlsx");
  };

  const resetAddForm = () => {
    setAddName("");
    setAddKlas("");
    setAddNewKlas("");
    setAddFather("");
    setAddPhone("");
  };

  const submitAdd = async () => {
    const name = addName.trim();
    const klasName = (addKlas === "__new__" ? addNewKlas : addKlas).trim();
    if (!name) {
      toast({ title: "Missing name", description: "Please enter the student's name", variant: "destructive" });
      return;
    }
    if (!klasName) {
      toast({ title: "Missing class", description: "Please pick or type a class", variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      await addTalmid({ fullName: name, klasName, father: addFather, phone: addPhone });
      toast({ title: "Student added", description: `${name} · ${klasName}` });
      resetAddForm();
      setAddOpen(false);
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Failed to add student",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-heb text-3xl font-bold text-slate-900">Students</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} of {talmidim.length} students · {klasn.length} classes</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />Excel Template
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />Import Excel
          </Button>
          <Button onClick={() => setAddOpen(true)} className="gradient-blue-mild text-white" data-testid="add-student-btn">
            <UserPlus className="w-4 h-4 mr-2" />Add Student
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search a student..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={klasFilter} onValueChange={setKlasFilter}>
              <SelectTrigger><SelectValue placeholder="All Classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {klasn.map((k) => <SelectItem key={k.id} value={String(k.id)}><span dir="rtl">{k.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : grouped.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500 mb-4">No students yet</div>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => setAddOpen(true)} className="gradient-blue-mild text-white">
                <UserPlus className="w-4 h-4 mr-2" />Add Student
              </Button>
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />Import from Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" defaultValue={grouped.map((g) => `k-${g.klas.id}`)} className="space-y-3">
          {grouped.map((g) => (
            <AccordionItem key={g.klas.id} value={`k-${g.klas.id}`} className="border-slate-200 border rounded-xl bg-white overflow-hidden">
              <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-slate-50">
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div className="w-10 h-10 rounded-lg gradient-blue-mild text-white flex items-center justify-center font-serif-heb font-bold" dir="rtl">
                    {g.klas.name.slice(-3).trim() || g.klas.id}
                  </div>
                  <div className="flex-1">
                    <div className="font-serif-heb text-lg font-bold text-slate-900" dir="rtl">{g.klas.name}</div>
                    <div className="text-xs text-slate-500">{g.list.length} students</div>
                  </div>
                  <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">{g.list.length}</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left w-10">#</th>
                        <th className="px-4 py-2 text-left">Student</th>
                        <th className="px-4 py-2 text-left">Phone</th>
                        <th className="px-4 py-2 text-left">Late Passes</th>
                        <th className="px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.list.map((t, idx) => (
                        <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-sm text-slate-500">{idx + 1}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-semibold" dir="rtl">{(t.firstName || t.fullName)[0]}</div>
                              <div className="font-medium text-slate-800" dir="rtl">{t.fullName}</div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-slate-600 font-en">{t.phone || "—"}</td>
                          <td className="px-4 py-2.5">
                            <Badge className={(countsByTalmid[t.id] || 0) > 3 ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                              {countsByTalmid[t.id] || 0}
                            </Badge>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1">
                              <Link to="/new-pass">
                                <Button size="sm" variant="outline"><FilePlus2 className="w-3.5 h-3.5 mr-1" />New Pass</Button>
                              </Link>
                              <Button size="sm" variant="ghost" onClick={() => deleteTalmid(t.id)} title="Delete">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview([]); setParseError(""); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif-heb text-2xl">Import Students from Excel</DialogTitle>
            <DialogDescription>
              Column 1: Student name · Column 2: Class name · Students are automatically sorted into their classes
            </DialogDescription>
          </DialogHeader>

          {importPreview.length === 0 ? (
            <div className="space-y-4">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onFileChange}
                className="hidden"
              />
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/30 rounded-xl p-10 text-center cursor-pointer transition-colors"
              >
                <FileSpreadsheet className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <div className="font-medium text-slate-800">Click to choose a file</div>
                <div className="text-xs text-slate-500 mt-1">or drag &amp; drop a .xlsx, .xls, .csv file</div>
              </div>

              {parseError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {parseError}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-600">
                <div className="font-medium text-slate-700 mb-2">Expected Excel format:</div>
                <table className="w-full border border-slate-300 text-center text-[11px]">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1">A (Student Name)</th>
                      <th className="border border-slate-300 px-2 py-1">B (Class)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td className="border border-slate-200 px-2 py-1" dir="rtl">מרדכי גאלדבערג</td><td className="border border-slate-200 px-2 py-1" dir="rtl">כתה א'</td></tr>
                    <tr><td className="border border-slate-200 px-2 py-1" dir="rtl">שלמה פרידמאן</td><td className="border border-slate-200 px-2 py-1" dir="rtl">כתה א'</td></tr>
                    <tr><td className="border border-slate-200 px-2 py-1" dir="rtl">אברהם וויס</td><td className="border border-slate-200 px-2 py-1" dir="rtl">כתה ב'</td></tr>
                  </tbody>
                </table>
                <div className="mt-2 text-slate-500">If the first row contains headers, they're auto-detected and skipped.</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-800 text-sm">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>{importPreview.length} students ready to import</span>
              </div>

              <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">#</th>
                      <th className="px-3 py-2 text-left">Student</th>
                      <th className="px-3 py-2 text-left">Class</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 100).map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-1.5 font-medium" dir="rtl">{it.name}</td>
                        <td className="px-3 py-1.5"><Badge variant="secondary"><span dir="rtl">{it.klas}</span></Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importPreview.length > 100 && (
                  <div className="p-2 text-xs text-slate-500 text-center border-t">... and {importPreview.length - 100} more</div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Checkbox checked={importReplace} onCheckedChange={(v) => setImportReplace(!!v)} />
                <div>
                  <div className="font-medium text-amber-900">Replace all existing students</div>
                  <div className="text-xs text-amber-700">If checked, all existing students will be deleted and replaced with this list</div>
                </div>
              </label>
            </div>
          )}

          <DialogFooter>
            {importPreview.length > 0 && (
              <Button variant="outline" onClick={() => { setImportPreview([]); setParseError(""); }}>
                Different File
              </Button>
            )}
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            {importPreview.length > 0 && (
              <Button onClick={doImport} disabled={importing} className="gradient-blue-mild text-white">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import {importPreview.length} students
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetAddForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-heb text-2xl">Add a Student</DialogTitle>
            <DialogDescription>
              Manually add one student. Fields marked * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">
                Student Name *
              </label>
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="למשל: דוד כהן"
                dir="rtl"
                autoFocus
                data-testid="add-student-name-input"
                onKeyDown={(e) => { if (e.key === "Enter" && addKlas && addName.trim()) submitAdd(); }}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">
                Class *
              </label>
              <Select value={addKlas} onValueChange={setAddKlas}>
                <SelectTrigger data-testid="add-student-klas-select">
                  <SelectValue placeholder="Pick an existing class or add a new one" />
                </SelectTrigger>
                <SelectContent>
                  {klasn.map((k) => (
                    <SelectItem key={k.id} value={k.name}>
                      <span dir="rtl">{k.name}</span>
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">
                    <span className="text-blue-600 font-medium">+ Add a new class…</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {addKlas === "__new__" && (
                <Input
                  className="mt-2"
                  value={addNewKlas}
                  onChange={(e) => setAddNewKlas(e.target.value)}
                  placeholder="נייע כיתה, למשל: כתה ח'"
                  dir="rtl"
                  data-testid="add-student-new-klas-input"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  Father Name
                </label>
                <Input
                  value={addFather}
                  onChange={(e) => setAddFather(e.target.value)}
                  placeholder="פאטער"
                  dir="rtl"
                  data-testid="add-student-father-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  Phone
                </label>
                <Input
                  value={addPhone}
                  onChange={(e) => setAddPhone(e.target.value)}
                  placeholder="718-555-1234"
                  className="font-en"
                  data-testid="add-student-phone-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button
              onClick={submitAdd}
              disabled={adding}
              className="gradient-blue-mild text-white"
              data-testid="add-student-submit-btn"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Add Student
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
